import { memo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { Briefcase, Mail } from 'lucide-react';
import { useServices } from '@/hooks/use-content';
import type { ServiceRow as Service } from '@/types/database';
import { servicePages } from '@/lib/service-pages';
import SectionEyebrow from './SectionEyebrow';

const ServiceCardSkeleton = () => (
  <Card className="p-6 h-full">
    <div className="flex flex-col h-full gap-4">
      <Skeleton className="h-8 w-8 rounded" />
      <div className="space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
      <Skeleton className="h-5 w-16 rounded-full mt-auto" />
    </div>
  </Card>
);

const ServiceCard = memo(({ service }: { service: Service }) => {
  return (
    <a
      href={service.link}
      target="_blank"
      rel="noopener noreferrer"
      className="block h-full"
    >
      <Card className="p-6 h-full hover:shadow-card hover:-translate-y-2 transition-all duration-300 group cursor-pointer border-2 hover:border-primary/50">
        <div className="flex flex-col h-full gap-4">
          <div className="text-primary group-hover:scale-110 transition-transform">
            <Briefcase className="h-8 w-8" />
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-2">{service.title}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {service.description}
            </p>
          </div>
          <div className="mt-auto">
            <Badge className={service.platform === 'fiverr' ? 'bg-[#1DBF73]' : 'bg-[#6C63FF]'}>
              {service.platform === 'fiverr' ? 'Fiverr' : 'Upwork'}
            </Badge>
          </div>
        </div>
      </Card>
    </a>
  );
});
ServiceCard.displayName = 'ServiceCard';

const Services = () => {
  const { t } = useTranslation();
  const { data: servicesData, isLoading } = useServices();

  return (
    <section id="services" className="py-14 sm:py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <SectionEyebrow index="09" label={t('services.eyebrow')} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('services.title')}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            <Trans i18nKey="services.description" components={{ b: <strong /> }} />
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {isLoading
            ? Array.from({ length: 4 }).map((_, idx) => <ServiceCardSkeleton key={idx} />)
            : servicesData?.map((service) => <ServiceCard key={service.id} service={service} />)}
        </div>

        <nav aria-label="Service pages" className="flex flex-wrap justify-center gap-x-6 gap-y-2 max-w-4xl mx-auto mt-10">
          {servicePages.map((page) => (
            <Link
              key={page.slug}
              to={`/services/${page.slug}`}
              className="text-sm font-medium text-primary hover:underline"
            >
              {page.label}
            </Link>
          ))}
        </nav>

        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-6">{t('services.lookingForCustom')}</p>
          <Button
            size="lg"
            className="bg-gradient-primary hover:scale-[1.03] transition-all shadow-lg"
            onClick={() => window.location.href = 'mailto:ahmadyousafzai30@gmail.com'}
          >
            <Mail className="mr-2 h-4 w-4" />
            {t('services.contactMe')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;
